import { useCallback, useEffect, useState } from "react";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Form, InputGroup, Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import type z from "zod";
import { api, type UsersSortOptions } from "../../utils/api";
import { getDeleteUserCustomErrorMessage } from "../../utils/errorMessages";
import { schemas } from "../../api/generated";
import { type PaginationState, type SortingState } from "@tanstack/react-table";
import { useDebounce } from "../../utils/useDebounce";
import { UsersTable } from "../tables/UsersTable";
import { ConfirmationModal } from "../modals/ConfirmationModal";

const ProblemDetails = schemas.ProblemDetails;
type User = z.infer<typeof schemas.User>;

function Users({ token }: { token: string }) {
  const [data, setData] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [promotionUser, setPromotionUser] = useState<User | null>(null);
  const [canPromoteUsers, setCanPromoteUsers] = useState(false);
  const [showSignOutAll, setShowSignOutAll] = useState(false);
  const [isSigningOutAll, setIsSigningOutAll] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalUsers, setTotalUsers] = useState(0);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [sorting, setSorting] = useState<SortingState>([
    { id: "username", desc: false },
  ]);

  const getAllUsers = useCallback(
    (
      token: string,
      searchTerm: string = "",
      limit: number = 1000,
      offset?: number,
      sort?: UsersSortOptions
    ) => {
      return api
        .getUsers(
          token,
          limit,
          offset,
          sort,
          searchTerm ? searchTerm : undefined
        )
        .then((response) => {
          setData(response.data);
          setTotalUsers(response.metadata?.totalCount);
          setCanPromoteUsers(response.capabilities?.canPromoteUsers === true);
        })
        .catch(() => {
          setCanPromoteUsers(false);
          return toast.error(
            "Failed to load users. Please refresh the page to try again."
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    []
  );

  useEffect(() => {
    getAllUsers(
      token,
      debouncedSearchTerm,
      pagination.pageSize,
      pagination.pageIndex * pagination.pageSize,
      sorting.length > 0
        ? ((sorting[0]?.desc
            ? `${sorting[0].id}_desc`
            : `${sorting[0].id}_asc`) as UsersSortOptions)
        : undefined
    ).catch((error) => {
      console.warn("Failed to fetch users.", error);
    });
  }, [token, debouncedSearchTerm, getAllUsers, pagination, sorting]);

  // // Run getAllUsers when page becomes visible
  // useOnPageVisible(() => {
  //   console.log("Page became visible again, refreshing users list.");
  //   getAllUsers(token, debouncedSearchTerm).catch((error) => {
  //     console.warn("Failed to fetch users on visibility change.", error);
  //   });
  // });

  const handleUserDelete = useCallback(async () => {
    if (!selectedUser) {
      console.warn("No user was selected for deletion.");
      return;
    }

    setIsLoading(true);
    try {
      await api.deleteUser(token, selectedUser?._id);

      setData((prevData) =>
        prevData.filter((user) => user._id !== selectedUser?._id)
      );
      setSelectedUser(null);
      toast.success("User deleted successfully.");

      // refetch users
      await getAllUsers(token, debouncedSearchTerm).catch(() => {
        console.warn("Failed to fetch users on visibility change.");
      });
    } catch (error) {
      const message = await ProblemDetails.parseAsync(error)
        .then(getDeleteUserCustomErrorMessage)
        .catch(() => "An error occurred while deleting the user.");
      toast.error(message);
    } finally {
      setShowConfirmation(false);
      setIsLoading(false);
    }
  }, [token, selectedUser, debouncedSearchTerm, getAllUsers]);

  const handleUserPromotion = useCallback(async () => {
    if (!promotionUser) return;

    try {
      await api.promoteUser(token, promotionUser.id);
      toast.success(
        "User promoted to Admin. New access takes effect at their next login."
      );
      await getAllUsers(
        token,
        debouncedSearchTerm,
        pagination.pageSize,
        pagination.pageIndex * pagination.pageSize,
        sorting.length > 0
          ? ((sorting[0]?.desc
              ? `${sorting[0].id}_desc`
              : `${sorting[0].id}_asc`) as UsersSortOptions)
          : undefined
      );
    } catch (error) {
      const message = await ProblemDetails.parseAsync(error)
        .then(
          (problem) =>
            problem.detail ?? "An error occurred while promoting the user."
        )
        .catch(() => "An error occurred while promoting the user.");
      toast.error(message);
    } finally {
      setPromotionUser(null);
    }
  }, [
    token,
    promotionUser,
    debouncedSearchTerm,
    getAllUsers,
    pagination,
    sorting,
  ]);

  const handleSignOutAll = useCallback(async () => {
    setIsSigningOutAll(true);
    try {
      await api.signOutAllSessions(token);
      toast.success("All users have been signed out and must sign in again.");
    } catch {
      toast.error("Failed to sign out all users.");
    } finally {
      setIsSigningOutAll(false);
      setShowSignOutAll(false);
    }
  }, [token]);

  if (isLoading) {
    return (
      <Spinner animation="border" role="status">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-end mb-2">
        <Button
          variant="outline-danger"
          size="sm"
          onClick={() => setShowSignOutAll(true)}
        >
          Sign out all users
        </Button>
      </div>

      <InputGroup className="mb-3">
        <InputGroup.Text>
          <label htmlFor="search-users" title="Search users">
            <FontAwesomeIcon icon={faSearch} />
          </label>
        </InputGroup.Text>
        <Form.Control
          id="search-users"
          type="text"
          placeholder="Search users"
          value={searchTerm}
          maxLength={254}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </InputGroup>

      <UsersTable
        data={data}
        totalUsers={totalUsers}
        setSelectedUser={(user) => setSelectedUser(user)}
        setShowConfirmation={(show) => setShowConfirmation(show)}
        onPromoteUser={setPromotionUser}
        canPromoteUsers={canPromoteUsers}
        onTableChange={(pagination, sorting) => {
          setPagination(pagination);
          setSorting(sorting);

          // Alternatively, fetch users here or dispatch action instead of in useEffect
        }}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        sortBy={sorting?.[0]?.id}
        sortDescending={sorting?.[0]?.desc}
      />

      <Modal show={showConfirmation} onHide={() => setShowConfirmation(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure want to delete this user?</Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            disabled={isLoading}
            onClick={() => setShowConfirmation(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={isLoading}
            onClick={handleUserDelete}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmationModal
        show={promotionUser !== null}
        onShow={(show) => {
          if (!show) setPromotionUser(null);
        }}
        title="Promote User"
        prompt={`Promote ${promotionUser?.email ?? "this user"} to Admin?`}
        warning="Admin access includes user management and attack library operations."
        confirmButtonText="Promote"
        onConfirmed={handleUserPromotion}
      />

      <Modal show={showSignOutAll} onHide={() => setShowSignOutAll(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Sign out all users</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This immediately ends every active session, including your own.
          Everyone will need to sign in again. Continue?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            disabled={isSigningOutAll}
            onClick={() => setShowSignOutAll(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={isSigningOutAll}
            onClick={handleSignOutAll}
          >
            Sign out all users
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default Users;
